import Base from '@studiometa/js-toolkit';
import matrix from '@studiometa/js-toolkit/utils/css/matrix';

/**
 * @typedef {Object} HeaderRef
 * @property {HTMLElement} toggle
 * @property {HTMLElement} menu
 */

/**
 * @typedef {Object} HeaderInterface
 * @property {HeaderRef} $refs
 * @property {Number} height
 * @property {Number} offset
 * @property {Boolean} open
 * @property {Boolean} hasTopBar
 */

/**
 * Homepage class.
 */
export default class Header extends Base {
  /**
   * Class config
   * @type {Object}
   */
  static config = {
    name: 'Header',
    refs: ['toggle', 'menu'],
  };

  /**
   * Initialize the component's behavior.
   *
   * @this {Header & HeaderInterface}
   */
  mounted() {
    this.offset = 0;
    this.$refs.toggle.addEventListener('click', this.toggleMenu);

    this.hasTopBar = this.$el.dataset.hasTopBar === '1';

    this.setDimensions();

    this.lastScrollY = window.scrollY;
    window.addEventListener('scroll', this.onScroll);
  }

  /**
   * Set dimensions.
   *
   * @this {Header & HeaderInterface}
   */
  setDimensions() {
    this.height = this.$el.getBoundingClientRect().height;
  }

  /**
   * Window scroll event.
   *
   * @this {Header & HeaderInterface}
   */
  onScroll() {
    if (window.scrollY >= 0) {
      if (this.lastScrollY < window.scrollY) {
        this.offset = Math.min(this.height, this.offset + window.scrollY - this.lastScrollY);
      } else {
        this.offset = Math.max(0, this.offset + window.scrollY - this.lastScrollY);
      }
    }

    this.$el.style.transform = matrix({
      translateY: -this.offset,
    });

    if (window.scrollY - this.offset <= 0) {
      if (!this.$el.classList.contains('header--transparent')) {
        this.$el.classList.add('header--transparent');
      }
    } else if (this.$el.classList.contains('header--transparent')) {
      this.$el.classList.remove('header--transparent');
    }

    this.$el.setAttribute('data-offset', (this.height - this.offset).toString());
    this.lastScrollY = window.scrollY;
  }

  /**
   * Window resized event.
   *
   * @this {Header & HeaderInterface}
   */
  resized() {
    this.setDimensions();
  }

  /**
   * Toggle mobile menu.
   *
   * @this {Header & HeaderInterface}
   */
  toggleMenu() {
    this.open = !this.open;

    if (this.open) {
      this.offset = 0;
      this.$el.style.transform = matrix({
        translateY: -this.offset,
      });

      this.$el.classList.add('header--open');
      document.body.style.overflow = 'hidden';
      this.$refs.menu.style.height = `${window.innerHeight - (72 + (this.hasTopBar ? 32 : 0))}px`;
    } else {
      this.$el.classList.remove('header--open');
      document.body.style.overflow = '';
      this.$refs.menu.style.height = '';
    }
  }

  /**
   * Unbind all events on destroy.
   *
   * @this {Header & HeaderInterface}
   * @return {Header} The Header instance.
   */
  destroyed() {
    return this;
  }
}
